import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Paths } from "src/paths";
import { SequenceService } from "@services/sequence/sequenceService";
import { KeyDateService } from "@services/engagements/keyDateService";

type Props = {
  launchId: number;
  engagementId: number;
  channel: "email" | "sms";
  /** The channel's own switch. The link is dead while it is off. */
  enabled: boolean;
};

/*
    What this channel will actually send, on the Campaign page (MPN-2203).

    The Email and SMS cards were built for one mailing at publish. Sequences changed what the
    channel means: the number worth showing is not "one per person" but how many messages are
    scheduled and when the first one lands. Both cards show the same line, so it lives here
    rather than twice.

    "Configure sequences" is deliberately dead while the channel is off. Switching a playbook
    on for a channel that cannot send produces a screenful of touches that will never go, and
    the acceptance criterion says so outright.
*/
export function ChannelSequenceSummary({ launchId, engagementId, channel, enabled }: Props) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState("");

  useEffect(() => {
    if (!launchId) return;
    let cancelled = false;

    void Promise.allSettled([
      new SequenceService().getLaunchSequences(launchId),
      new KeyDateService().listForCampaign(launchId),
    ]).then(([seqs, dates]) => {
      if (cancelled || seqs.status !== "fulfilled") return;
      const keyDates = dates.status === "fulfilled" ? (dates.value ?? []) : [];

      const live = (seqs.value ?? []).filter((row) => row.is_on);
      const mine = live.flatMap((row) =>
        row.touches.filter((touch) => touch.channel === channel && touch.is_on),
      );
      if (mine.length === 0) {
        setSummary("No sequences use this channel yet");
        return;
      }

      /*
          The earliest send still ahead of us, from the campaign's own key dates. A touch
          anchored to a date this campaign has not set has no send date, so it is skipped
          rather than guessed at — the same rule the Email & SMS tab applies.
      */
      const sends: number[] = [];
      for (const touch of mine) {
        const keyDate = keyDates.find(
          (k) => k.key_date_type_id === touch.key_date_type_id && k.date,
        );
        if (!keyDate?.date) continue;
        const [y, m, d] = String(keyDate.date).slice(0, 10).split("-").map(Number);
        const when = new Date(y, m - 1, d);
        const shift =
          touch.direction === "on"
            ? 0
            : touch.direction === "before"
              ? -touch.offset_days
              : touch.offset_days;
        when.setDate(when.getDate() + shift);
        if (when.getTime() >= Date.now()) sends.push(when.getTime());
      }

      const sequences = live.filter((row) =>
        row.touches.some((touch) => touch.channel === channel && touch.is_on),
      ).length;

      const parts = [
        `${sequences} ${sequences === 1 ? "sequence" : "sequences"}`,
        `${mine.length} ${mine.length === 1 ? "message" : "messages"}`,
      ];
      if (sends.length) {
        parts.push(
          `next send ${new Date(Math.min(...sends)).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}`,
        );
      }
      setSummary(parts.join(" · "));
    });

    return () => {
      cancelled = true;
    };
  }, [launchId, channel]);

  return (
    <div className="d-flex align-items-center justify-content-between gap-2 mt-3 pt-3 border-top">
      <span className="small text-muted">{summary || "Loading sequences…"}</span>
      {/*
          Said in text, not in a tooltip. A `title` on a disabled button never appears —
          the element fires no hover events — so the one explanation of why the link is
          dead was invisible to everybody who needed it.
      */}
      {!enabled && (
        <span className="small text-muted fst-italic">
          Switch this channel on to configure its sequences
        </span>
      )}
      <button
        type="button"
        className="btn btn-link btn-sm p-0"
        disabled={!enabled}
        onClick={() =>
          navigate(
            `${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${engagementId}/email-sms/${launchId}`,
          )
        }
      >
        Configure sequences →
      </button>
    </div>
  );
}

export default ChannelSequenceSummary;
